import { ref, computed, inject, watch } from 'vue'
import { getAvailableFields } from '@/configurations/financials/tamaro'
import { loadScript, centsToAmount } from '@/utils/utils'
import { get as _get, set as _set, cloneDeep as _cloneDeep } from 'lodash'

const tamaroSymbol = Symbol()

export const createTamaro = {
  install: app => {
    const version = process.env.VUE_APP_TAMARO_VERSION || 'latest'
    const fields = getAvailableFields()

    const is_loading = ref(false)
    const disposers = ref([])
    const config = ref()
    const payment_form = ref()
    const custom_keys = ref([])

    const required_fields = ref([])
    const validation_errors = ref({})

    const requiredFields = computed(() => Object.entries(fields).reduce((acc, [key, field]) => required_fields.value.includes(key) ? [...acc, field] : acc, []))
    const requiredModels = computed(() => requiredFields.value.map(field => field.model))

    const render = (id, configuration = {}) => new Promise(async (resolve, reject) => {
      is_loading.value = true
      config.value = configuration

      try {
        if (!window.rnw?.tamaro) {
          await loadScript(`https://tamaro.raisenow.com/koalect/${version}/widget.js`)

          setupEvents()

          await window.rnw.tamaro.createWidget(config.value)
        }

        window.rnw.tamaro.renderWidget(window.rnw.tamaro.instance, id)

        is_loading.value = false

        resolve()
      } catch {
        reject()
      }
    })

    const destroy = () => {
      disposers.value.forEach(dispose => dispose?.())
      disposers.value = []

      window.rnw?.tamaro?.destroy()

      payment_form.value = {}
    }

    const validate = async () => window.rnw?.tamaro.instance?.paymentForm.validateBeforeSend()

    const retryPayment = () => window.rnw?.tamaro.instance?.retryPayment()

    const send = async () => window.rnw?.tamaro.instance?.paymentForm.send()

    const setupEvents = () => {
      disposers.value.push(
        window.rnw?.tamaro.events.afterCreate.subscribe(async event => {
          const tamaro = event.data.api
          const { reaction, comparer, toJS } = await window.rnw.tamaro.libs.mobx()

          disposers.value.push(
            reaction(
              () => toJS(tamaro.paymentForm.allValidationErrors),
              errors => validation_errors.value = errors,
              { fireImmediately: true, equals: comparer.structural }
            )
          )

          disposers.value.push(
            reaction(
              () => toJS(tamaro.paymentForm.requiredFields),
              fields => required_fields.value = fields,
              { fireImmediately: true, equals: comparer.structural }
            )
          )

          disposers.value.push(
            watch(
              config,
              config => tamaro.extendConfig(_cloneDeep(config)),
              { deep: true, immediate: false }
            )
          )
        })
      )
    }

    const getPaymentMethod = () => window.rnw?.tamaro.instance?.paymentForm.getPaymentMethod()

    const getPaymentType = () => window.rnw?.tamaro.instance?.paymentForm.getPaymentType()

    const setAmount = amount => window.rnw?.tamaro.instance?.paymentForm.setAmount(centsToAmount(amount))

    const setAmounts = amounts => config.value = _set(config.value, 'amounts', amounts)

    const setPaymentFormData = (data = {}) => {
      if (!window.rnw?.tamaro.instance?.paymentForm) return

      cleanPaymentFormData(data)

      Object.keys(data).forEach(key => {
        _set(window.rnw.tamaro.instance.paymentForm, `data.${key}`, data[key])

        if (!custom_keys.value.includes(key)) custom_keys.value.push(key)
      })
    }

    const setPaymentFormStaticData = (data = {}) => {
      if (!window.rnw?.tamaro.instance?.paymentForm) return

      Object.keys(data).forEach(key => _set(window.rnw.tamaro.instance.paymentForm, `data.${key}`, data[key]))
    }

    const cleanPaymentFormData = (data = {}) => {
      if (!window.rnw?.tamaro.instance?.paymentForm) return

      custom_keys.value.forEach(key => {
        if (!(key in data)) {
          delete window.rnw.tamaro.instance.paymentForm.data[key]

          custom_keys.value = custom_keys.value.filter(k => k !== key)
        }
      })
    }

    const afterRender = callback => {
      if (callback) disposers.value.push(window.rnw?.tamaro.events.afterRender.subscribe(() => callback()))
    }

    const beforePaymentValidateAndSend = callback => {
      if (callback) disposers.value.push(window.rnw?.tamaro.events.beforePaymentValidateAndSend.subscribe(() => callback()))
    }

    const paymentMethodChanged = callback => {
      if (callback) {
        callback(getPaymentMethod())

        disposers.value.push(window.rnw?.tamaro.events.paymentMethodChanged.subscribe(() => callback(getPaymentMethod())))
      }
    }

    const paymentComplete = callback => {
      disposers.value.push(
        window.rnw?.tamaro.events.fetchPaymentDataEnd.subscribe(e => {
          const key = getPaymentType() === 'recurring' ? 'epmsSubscriptionInfo' : 'epmsPaymentInfo'
          const transaction_id = _get(e, `data.${key}.custom_parameters.koa_transaction_id`)
          const status = getPaymentType() === 'recurring' ? _get(e, `data.${key}.status`) : _get(e, `data.${key}.last_status`)

          if (!status || !transaction_id) return
          if (callback) callback({ status, transaction_id })

          retryPayment()
        })
      )
    }

    app.provide(tamaroSymbol, {
      fields,
      is_loading,
      required_fields,
      validation_errors,
      requiredFields,
      requiredModels,
      render,
      destroy,
      validate,
      retryPayment,
      send,
      setAmount,
      setAmounts,
      setPaymentFormData,
      setPaymentFormStaticData,
      afterRender,
      beforePaymentValidateAndSend,
      paymentMethodChanged,
      paymentComplete
    })
  }
}

export const useTamaro = () => inject(tamaroSymbol)

